import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { array, arrayOf, bool, func, shape, string, oneOf, object } from 'prop-types';
import { compose } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import Slider from 'react-slick';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, intlShape, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
} from '../../util/urlHelpers';
import { convertMoneyToNumber } from '../../util/currency';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import { getListingsById, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import { setActiveListing } from '../../containers/SearchPage/SearchPage.duck';

import {
  H4,
  Page,
  NamedLink,
  NamedRedirect,
  Footer,
  OrderPanel,
  LayoutSingleColumn,
  ListingCard,
} from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
  showMerchantListing,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
} from './ListingPage.shared';
import SectionTextMaybe from './SectionTextMaybe';
import SectionGallery from './SectionGallery';

import css from './ListingPage.module.css';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { getMerchantListingId } from '../../util/helper';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

export const ListingPageComponent = (props => {
  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    monthlyTimeSlots,
    onFetchTimeSlots,
    listingConfig: listingConfigProp,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    bookmarkedData,
    onUpdateProfile,
    couponCodeArr,
    merchnatsListings,
    isMapVariant,
    onActivateListing,
    couponListings,
  } = props;

  // prop override makes testing a bit easier
  // TODO: improve this when updating test setup
  const listingConfig = listingConfigProp || config.listing;
  const listingId = new UUID(rawParams.id);

  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));
  const merchnatBussinessEmail = currentListing?.attributes?.publicData?.businessEmail;
  const merchnatContactNumber = currentListing?.attributes?.publicData?.businessNumber;
  const merchnatAddress = currentListing?.attributes?.publicData?.location?.address;
  const isCoupon =
    currentListing && currentListing?.attributes?.publicData?.listingType == 'COUPON';
  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };
  const isProductListing = currentListing?.attributes?.publicData?.category == 'Products';

  const bookmarks =
    (currentUser &&
      currentUser.attributes &&
      currentUser.attributes.profile &&
      currentUser.attributes.profile.publicData &&
      currentUser.attributes.profile.publicData.bookmarks) ||
    [];
  const [activeTab, setActiveTab] = useState(1);

  const handleWishlist = values => {
    const { quantity="1", deliveryMethod, discountedPrice = false } = values;
    
    const { id: listingId } = rawParams;

    function removeProperties(obj, propertiesToRemove) {
      const modifiedObject = { ...obj };
      propertiesToRemove.forEach(prop => delete modifiedObject[prop]);
      return modifiedObject;
    }
    const propertiesToRemove = [
      'discountedPrice',
      'newPrice',
      'quantity',
      'couponCode',
      'deliveryMethod',
    ];
    const modifiedObject = removeProperties(values, propertiesToRemove);

    if (!isAuthenticated && listingId) {
      let localBookmarks =
        typeof window !== 'undefined' &&
        window.localStorage.getItem('localBookmarks') &&
        window.localStorage.getItem('localBookmarks').length > 0
          ? window.localStorage.getItem('localBookmarks')
          : [];

      if (typeof localBookmarks === 'string') {
        localBookmarks =
          typeof window !== 'undefined' &&
          JSON.parse(window.localStorage.getItem('localBookmarks'));
      }

      const localIndex = localBookmarks && localBookmarks.findIndex(b => b.id == listingId);
      const hasItem = localBookmarks && localBookmarks.find(e => e.id == listingId);

      if (hasItem) {
        localBookmarks && localBookmarks.splice(localIndex, 1);
        const removedBookmarks = Array.from(new Set(localBookmarks));
        typeof window !== 'undefined' &&
          window.localStorage.setItem('localBookmarks', JSON.stringify(removedBookmarks));
      } else if (isProductListing && quantity) {
        localBookmarks &&
          localBookmarks.push({
            id: listingId,
            quantity: quantity,
            deliveryMethod: deliveryMethod,
            discountedPrice: discountedPrice?.amount,
            modifiedObject,
          });
        const addedBookmarks = Array.from(new Set(localBookmarks));
        typeof window !== 'undefined' &&
          window.localStorage.setItem('localBookmarks', JSON.stringify(addedBookmarks));
      } else if (!isProductListing && quantity) {
        localBookmarks &&
          localBookmarks.push({
            id: listingId,
            quantity: quantity,
            deliveryMethod: deliveryMethod,
            discountedPrice: discountedPrice?.amount,
            modifiedObject,
          });
        const addedBookmarks = Array.from(new Set(localBookmarks));
        typeof window !== 'undefined' &&
          window.localStorage.setItem('localBookmarks', JSON.stringify(addedBookmarks));
      }
    }

    const bookmarks =
      (currentUser &&
        currentUser.attributes &&
        currentUser.attributes.profile &&
        currentUser.attributes.profile.publicData &&
        currentUser.attributes.profile.publicData.bookmarks) ||
      [];

    const index = bookmarks && bookmarks.findIndex(b => b.id == listingId);

    if (listingId) {
      if (index > -1) {
        bookmarks && bookmarks.splice(index, 1);
        const removedBookmarks = Array.from(new Set(bookmarks));
        const profile = {
          publicData: {
            bookmarks: removedBookmarks,
          },
        };
        onUpdateProfile(profile);
      } else if (isProductListing && quantity && deliveryMethod) {
        bookmarks.push({
          id: listingId,
          quantity,
          deliveryMethod,
          discountedPrice: discountedPrice?.amount,
          modifiedObject: modifiedObject,
        });
        const addedBookmarks = Array.from(new Set(bookmarks));
        const profile = {
          publicData: {
            bookmarks: addedBookmarks,
          },
        };
        onUpdateProfile(profile);
      } else if (!isProductListing && quantity && deliveryMethod) {
        bookmarks.push({
          id: listingId,
          quantity,
          deliveryMethod,
          discountedPrice: discountedPrice?.amount,
          modifiedObject: modifiedObject,
        });
        const addedBookmarks = Array.from(new Set(bookmarks));
        const profile = {
          publicData: {
            bookmarks: addedBookmarks,
          },
        };
        onUpdateProfile(profile);
      }
    }
  };

  const author = currentListing?.author;
  const merchantListingId = author && getMerchantListingId(author);
  const dispatch = useDispatch();

  useEffect(() => {
    if (merchantListingId) dispatch(showMerchantListing(merchantListingId));
  }, [merchantListingId]);

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  // Move useMemo before any conditional returns to comply with Rules of Hooks
  const settings = useMemo(() => ({
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    initialSlide: 0,
    arrows: true,
    // Enable on-demand lazy loading of slides' images to reduce initial work
    lazyLoad: 'ondemand',
    responsive: [
      {
        breakpoint: 1600,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
          infinite: true,
          dots: false,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
          infinite: true,
          dots: false,
          arrows: false,
        },
      },
      {
        breakpoint: 600,
        settings: {
          infinite: false,
          slidesToShow: 2,
          slidesToScroll: 2,
          initialSlide: 2,
        },
      },
      {
        breakpoint: 480,
        settings: {
          infinite: true,
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  }), []);

  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;
 
  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const shippingChargesForOneItem = publicData?.shippingPriceInSubunitsOneItem / 100;
  const shippingChargesForAdditionalItem = publicData?.shippingPriceInSubunitsAdditionalItems / 100;
  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    // setInquiryModalOpen,
  });
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    // setInquiryModalOpen,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      const { ButtonClicked, ...rest } = values;
      
      if (['ADD_CART', 'REMOVE_CART'].includes(ButtonClicked)) {
        return handleWishlist(rest);
      }
      onSubmit(rest);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const schemaPriceMaybe = price
    ? {
        price: intl.formatNumber(convertMoneyToNumber(price), {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        priceCurrency: price.currency,
      }
    : {};
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability =
    currentStock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock';

  const createFilterOptions = options => options.map(o => ({ key: `${o.option}`, label: o.label }));

  const cartItems = currentUser?.attributes?.profile?.publicData?.cartItems;
  const localBookmarks =
    typeof window !== 'undefined' && JSON.parse(window.localStorage.getItem('localBookmarks'));
  const bookmarksArr = isAuthenticated ? bookmarks : localBookmarks;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 549px) 100vw',
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...schemaPriceMaybe,
          availability: schemaAvailability,
        },
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<Footer />}>
        <div className={css.contentWrapperForProductLayout}>
          <div className={css.mainColumnForProductLayout}>
            <SectionGallery
              listing={currentListing}
              variantPrefix={config.layout.listingImage.variantPrefix}
            />
            <div className={css.mobileHeading}>
              <H4 as="h1" className={css.orderPanelTitle}>
                <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
              </H4>
            </div>
            <div className={css.tabWrapper}>
              {isCoupon ? null : (
                <div>
                  <a className={activeTab == 1 && css.activeTab} onClick={() => setActiveTab(1)}>
                    Description
                  </a>
                  <a className={activeTab == 2 && css.activeTab} onClick={() => setActiveTab(2)}>
                    Shipping
                  </a>
                  <a className={activeTab == 3 && css.activeTab} onClick={() => setActiveTab(3)}>
                    Meet your Merchant
                  </a>
                </div>
              )}
            </div>
            {activeTab == 1 ? (
              <div className={css.myAccountWrapper}>
                <SectionTextMaybe text={description} showAsIngress />
              </div>
            ) : null}
            {activeTab === 2 ? (
              <div className={css.myAccountWrapper}>
                {publicData?.pickupEnabled && !publicData?.shippingEnabled && (
                  <div>
                    <p>No shipping available</p>
                    <p>Pickup location - {publicData?.location?.address}</p>
                  </div>
                )}

                {publicData?.pickupEnabled && publicData?.shippingEnabled && (
                  <div>
                    <p>Available for shipping and pickup</p>
                    <p>Shipping Charges for one item - ${shippingChargesForOneItem}</p>
                    <p>Shipping Charges for additional item - ${shippingChargesForAdditionalItem}</p>
                  </div>
                )}

                {!publicData?.pickupEnabled && publicData?.shippingEnabled && (
                  <div>
                    <p>Shipping Only, Pickup not available</p>
                    <p>Shipping Charges for one item - ${shippingChargesForOneItem}</p>
                    <p>Shipping Charges for additional item - ${shippingChargesForAdditionalItem}</p>
                  </div>
                )}
              </div>

            ) : null}

            {activeTab == 3 ? (
              <div className={css.myAccountWrapper}>
                <p>Business Email - {merchnatBussinessEmail}</p>
                <p>Business Contact Number - {merchnatContactNumber}</p>
                <p>Business Address - {merchnatAddress}</p>
                <div>
                  <NamedLink name="ProfilePage" params={{ id: currentListing?.author?.id?.uuid }}>
                    <button> View Merchant's Page</button>
                  </NamedLink>
                </div>
              </div>
            ) : null}
          </div>
          <div className={css.orderColumnForProductLayout}>
            <OrderPanel
              className={css.productOrderPanel}
              listing={currentListing}
              isOwnListing={isOwnListing}
              couponCodeArr={couponCodeArr}
              couponListings={couponListings}
              bookmarkedData={bookmarkedData}
              isCoupon={isCoupon}
              onSubmit={values => handleOrderSubmit(values)}
              authorLink={
                <NamedLink
                  className={css.authorNameLink}
                  name="ListingPage"
                  params={params}
                  to={{ hash: '#author' }}
                >
                  {authorDisplayName}
                </NamedLink>
              }
  
              title={<FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />}
              titleDesktop={
                <H4 as="h1" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H4>
              }
              author={ensuredAuthor}
              onManageDisableScrolling={onManageDisableScrolling}
              onContactUser={onContactUser}
              monthlyTimeSlots={monthlyTimeSlots}
              onFetchTimeSlots={onFetchTimeSlots}
              onFetchTransactionLineItems={onFetchTransactionLineItems}
              lineItems={lineItems}
              fetchLineItemsInProgress={fetchLineItemsInProgress}
              fetchLineItemsError={fetchLineItemsError}
              marketplaceCurrency={config.currency}
              dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
              marketplaceName={config.marketplaceName}
              cartItems={cartItems}
              currentUser={currentUser}
              isAuthenticated={isAuthenticated}
              handleWishlist={handleWishlist}
              bookmarks={bookmarksArr}
            />
          </div>
        </div>
        <div className={css.moreSliderWrapper}>
          <h2>More from other Merchant's</h2>
          <div>
            <Slider {...settings}>
              {merchnatsListings &&
                Array.isArray(merchnatsListings) &&
                merchnatsListings.map((l, ind) => (
                  <div key={ind} className={css.listingCard}>
                    <ListingCard
                      key={l?.id?.uuid}
                      listing={l}
                      renderSizes={cardRenderSizes(isMapVariant)}
                      setActiveListing={setActiveListing}
                    />
                  </div>
                ))}
            </Slider>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
});

ListingPageComponent.defaultProps = {
  currentUser: null,
  inquiryModalOpenForListingId: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  monthlyTimeSlots: null,
  sendInquiryError: null,
  listingConfig: null,
  lineItems: null,
  fetchLineItemsError: null,
};

ListingPageComponent.propTypes = {
  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,
  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  params: shape({
    id: string.isRequired,
    slug: string,
    variant: oneOf([LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT]),
  }).isRequired,

  isAuthenticated: bool.isRequired,
  currentUser: propTypes.currentUser,
  getListing: func.isRequired,
  getOwnListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,
  inquiryModalOpenForListingId: string,
  showListingError: propTypes.error,
  callSetInitialValues: func.isRequired,
  reviews: arrayOf(propTypes.review),
  fetchReviewsError: propTypes.error,
  monthlyTimeSlots: object,
  // monthlyTimeSlots could be something like:
  // monthlyTimeSlots: {
  //   '2019-11': {
  //     timeSlots: [],
  //     fetchTimeSlotsInProgress: false,
  //     fetchTimeSlotsError: null,
  //   }
  // }
  sendInquiryInProgress: bool.isRequired,
  sendInquiryError: propTypes.error,
  onSendInquiry: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,
  listingConfig: object,
  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
};

const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    couponCodeArr,
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    currentCouponPageResultIds,
    searchCouponInProgress,
    searchCouponListingsError,
    searchCouponParams,
    activeListingId,
  } = state.ListingPage;

  const merchnatsListings = getListingsById(state, currentPageResultIds);
  const couponListings = getListingsById(state, currentCouponPageResultIds);
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendInquiryInProgress,
    sendInquiryError,
    couponCodeArr,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    activeListingId,
    merchnatsListings,
    couponListings,
    searchCouponInProgress,
    searchCouponListingsError,
    searchCouponParams,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)),
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone)),
  onUpdateProfile: data => dispatch(updateProfile(data)),
  onActivateListing: listingId => dispatch(setActiveListing(listingId)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedListingPage);

export default ListingPage;
